import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import {
  Shield, Loader2, AlertCircle, Eye, EyeOff,
  Target, Clock, XCircle, Mail, FileText, Database,
  CheckCircle, Lock, Sparkles
} from "lucide-react";
import auditLogo from "../assets/zentis-logo.png";

function StatPill({ icon: Icon, value, label }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <Icon className="w-5 h-5 text-emerald-400" />
      <span className="text-lg font-extrabold text-white leading-none">{value}</span>
      <span className="text-[10px] font-semibold text-emerald-200 uppercase tracking-wider">{label}</span>
    </div>
  );
}

function MetricBadge({ icon: Icon, value, sub }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="text-sm font-extrabold text-white leading-none">{value}</div>
        <div className="text-[10px] text-emerald-300 font-medium tracking-wide uppercase mt-0.5">{sub}</div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Email and password are required."); return; }
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid credentials. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen w-full flex items-center justify-center p-4 sm:p-6 lg:p-8 bg-slate-950 text-slate-100 relative overflow-hidden"
    >
      {/* Background Orbs */}
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-teal-600/20 rounded-full blur-3xl pointer-events-none" />

      {/* Main Container */}
      <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-12 rounded-3xl overflow-hidden border border-emerald-500/20 bg-slate-900/80 backdrop-blur-2xl shadow-2xl relative z-10">

        {/* LEFT PANEL - Hero Showcase */}
        <div className="lg:col-span-7 p-8 lg:p-12 flex flex-col justify-between relative bg-gradient-to-br from-emerald-950/80 via-slate-900 to-slate-950 border-b lg:border-b-0 lg:border-r border-emerald-500/20">
          {/* Header & Logo */}
          <div className="flex flex-col items-start gap-3 relative z-10">
            <div className="flex items-center">
              <img src={auditLogo} alt="Zentis Logo" className="h-9 w-auto object-contain" />
            </div>

            <div>
              <span className="text-2xl font-black text-white tracking-tight block leading-none font-heading">AuditOS</span>
              <span className="text-[11px] font-bold text-emerald-300 tracking-widest uppercase block mt-1">Compliance Hub</span>
            </div>
          </div>

          {/* Center Copy */}
          <div className="my-8 lg:my-12 relative z-10 space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-semibold">
              <Sparkles className="w-3.5 h-3.5" /> Next-Gen Enterprise Assurance
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight tracking-tight">
              Continuous audit.<br />
              Zero blind spots.<br />
              <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">Full confidence.</span>
            </h1>
            <p className="text-sm text-slate-300 leading-relaxed max-w-md">
              Real-time control evaluation, automated evidence parsing, and AI audit teams working in synergy.
            </p>
          </div>

          {/* Metrics Footer */}
          <div className="pt-6 border-t border-emerald-500/20 grid grid-cols-2 sm:grid-cols-4 gap-4 relative z-10">
            <MetricBadge icon={Target} value="100%" sub="Coverage" />
            <MetricBadge icon={CheckCircle} value="75%" sub="Faster" />
            <MetricBadge icon={Clock} value="24/7" sub="Monitoring" />
            <MetricBadge icon={Shield} value="SOC 2" sub="Compliant" />
          </div>
        </div>

        {/* RIGHT PANEL - Login Form */}
        <div className="lg:col-span-5 p-8 lg:p-12 flex flex-col justify-center bg-slate-900/90">
          <div className="mb-6">
            <span className="text-xs font-bold text-emerald-400 tracking-widest uppercase block mb-1">
              Secure Access
            </span>
            <h2 className="text-2xl font-extrabold text-white tracking-tight">
              Sign in to AuditOS
            </h2>
            <p className="text-xs text-slate-400 mt-1">
              Enter your corporate credentials to launch your audit control room.
            </p>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-3 gap-2 p-3 rounded-2xl bg-emerald-500/5 border border-emerald-500/15 mb-6 text-center">
            <StatPill icon={Target} value="100%" label="Control Coverage" />
            <StatPill icon={Clock} value="24/7" label="Real-time" />
            <StatPill icon={XCircle} value="0" label="Zero Gaps" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div
                data-testid="login-error"
                className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/25 text-xs text-red-300 animate-fadeIn"
              >
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Email Field */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-200">
                Work Email
              </label>
              <div className="relative">
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="admin@audit.com"
                  autoFocus
                  data-testid="login-email-input"
                  className="w-full h-11 pl-3.5 pr-10 rounded-xl bg-slate-950/80 border border-slate-700/80 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                />
                <Mail className="w-4 h-4 absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-400 pointer-events-none" />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-1.5">
              <label className="block text-xs font-semibold text-slate-200">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  data-testid="login-password-input"
                  className="w-full h-11 pl-3.5 pr-10 rounded-xl bg-slate-950/80 border border-slate-700/80 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-emerald-400 hover:text-emerald-300"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center justify-between text-xs text-slate-300 pt-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={e => setRemember(e.target.checked)}
                  className="rounded border-slate-700 bg-slate-950 text-emerald-500 focus:ring-emerald-500/20 h-4 w-4"
                />
                <span>Remember me</span>
              </label>
              <button type="button" className="text-emerald-400 hover:underline font-medium">
                Forgot password?
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 font-bold text-sm text-white shadow-lg shadow-emerald-600/30 flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-2"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Shield className="w-4 h-4" />
              )}
              Sign In to Dashboard
            </button>
          </form>

          {/* Footer Trust Pill */}
          <div className="mt-6 text-center flex items-center justify-center gap-2 text-[11px] text-slate-500">
            <Lock className="w-3 h-3 text-emerald-500" />
            <span>256-Bit Encrypted</span>
            <span>•</span>
            <span>Enterprise Assurance</span>
          </div>
        </div>

      </div>
    </div>
  );
}

